import React, { useCallback, useMemo } from "react";
// components
import { SegmentedBar } from "#webapp/src/components/segmented-bar";
import { InfoTip } from "#webapp/src/components/tool-tip";
// time-allocation imports
import * as s from "./TimeAllocation.styles";

type TimeAllocationSegmentedBarProps = {
  totalMinutes: number | null;
  minutes: number | null;
  goalMinutes: number;
  label: string;
  color: string;
  description: string;
  animateIn?: boolean;
  hideJudgement?: boolean;
};

const labels = {
  FOCUS_TIME: "Focus Time",
  MEETINGS: "meetings",
  OTHER: "other events",
  FRAGMENTED_TIME: "fragmented time",
};

export const TimeAllocationSegmentedBar = ({
  totalMinutes,
  goalMinutes,
  label,
  color,
  description,
  minutes,
  hideJudgement,
}: TimeAllocationSegmentedBarProps) => {
  const showGoal =
    label === labels.FOCUS_TIME ||
    label === labels.FRAGMENTED_TIME ||
    (totalMinutes && totalMinutes > 0);

  const goalPercent = (goalMinutes / (totalMinutes || 1)) * 100;
  const goalStyle = {
    ...s.goalIndicator,
    left: `${goalPercent}%`,
    display: showGoal ? "block" : "none",
  };

  const getRoundedHours = useCallback((minutesToRound: number) => {
    if (minutesToRound > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutesToRound / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutesToRound / 30) / 2;
    }
  }, []);

  const segments = useMemo(() => {
    if (totalMinutes === null || minutes === null) return [];
    const labelForSegment = `${getRoundedHours(minutes)} hr ${label}`;
    const remainder = totalMinutes ? totalMinutes - minutes : 1;

    return [
      { color, value: minutes, tipLabel: labelForSegment },
      { color: s.barBg, value: remainder, isInset: true },
    ];
  }, [getRoundedHours, color, minutes, label, totalMinutes]);

  const renderJudgement = () => {
    if (totalMinutes === null || minutes === null) return null;
    const wantToBeatGoal = label === labels.FOCUS_TIME;
    if (hideJudgement) {
      return null;
    }

    const percentile = (minutes - goalMinutes) / (totalMinutes - goalMinutes);

    if (wantToBeatGoal) {
      if (minutes < goalMinutes) {
        return <div style={s.badLabel}>Low</div>;
      } else if (percentile < 0.3) {
        return <div style={s.goodLabel}>Ok</div>;
      } else if (percentile < 0.6) {
        return <div style={s.goodLabel}>Good</div>;
      } else {
        return <div style={s.goodLabel}>Great</div>;
      }
    } else {
      if (minutes > goalMinutes) {
        return <div style={s.badLabel}>High</div>;
      } else if (percentile > -0.05) {
        return <div style={s.goodLabel}>Ok</div>;
      } else if (percentile > -0.1) {
        return <div style={s.goodLabel}>Good</div>;
      } else {
        return <div style={s.goodLabel}>Great</div>;
      }
    }
  };

  if (!segments.length) return null;

  return (
    <div style={s.container}>
      <div style={s.legendRow}>
        <div style={s.focusHours}>{getRoundedHours(segments[0].value)} hr</div>
        <div>{label}</div>
        <div>
          <InfoTip text={description} iconStyle={s.tooltipIcon} tipType={"info"} />
        </div>
        {Boolean(showGoal) && renderJudgement()}
      </div>
      <div style={s.barContainer}>
        <div style={goalStyle} />
        <SegmentedBar segments={segments} animateIn={true} />
      </div>
    </div>
  );
};
